import { useEffect, useMemo, useRef, useState } from "react";
import { cadProvider } from "./cad";
import { Header } from "./components/Header";
import { ConfigurationPanel } from "./components/ConfigurationPanel";
import { HouseViewer } from "./components/HouseViewer";
import { MobileActionBar } from "./components/MobileActionBar";
import { QuoteModal } from "./components/QuoteModal";
import { StepNavigation } from "./components/StepNavigation";
import { SummaryPanel } from "./components/SummaryPanel";
import { useConfiguratorStore } from "./store";
import type { CadAssets, HouseConfiguration } from "./types";

const fallbackAssets: CadAssets = {
  model3dUrl: "/models/cbciso-layout-a-128.glb?v=3",
  floorPlanUrl: "/floorplans/layout-a.svg?v=3"
};

function decodeSharedConfiguration(): HouseConfiguration | null {
  const encoded = new URLSearchParams(window.location.search).get("config");
  if (!encoded) return null;
  try {
    return JSON.parse(decodeURIComponent(escape(atob(encoded)))) as HouseConfiguration;
  } catch {
    return null;
  }
}

export default function App() {
  const configuration = useConfiguratorStore((state) => state.configuration);
  const load = useConfiguratorStore((state) => state.load);
  const [assets, setAssets] = useState<CadAssets>(fallbackAssets);
  const [cadStatus, setCadStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const requestCounter = useRef(0);
  const geometryKey = useMemo(
    () => [
      configuration.modelId,
      configuration.areaM2,
      configuration.layoutId,
      configuration.bedrooms,
      configuration.bathrooms,
      configuration.office,
      configuration.roofMaterialId,
      configuration.terrace,
      configuration.carport
    ].join("|"),
    [
      configuration.areaM2,
      configuration.carport,
      configuration.bathrooms,
      configuration.bedrooms,
      configuration.layoutId,
      configuration.modelId,
      configuration.office,
      configuration.roofMaterialId,
      configuration.terrace
    ]
  );

  useEffect(() => {
    const shared = decodeSharedConfiguration();
    if (shared) {
      load(shared);
      setToast("Den delte konfiguration er indlæst");
    }
  }, [load]);

  const requestPreview = (immediate = false) => {
    const controller = new AbortController();
    const requestId = ++requestCounter.current;
    const timer = window.setTimeout(async () => {
      setCadStatus("loading");
      try {
        const result = await cadProvider.requestPreview(configuration, controller.signal);
        if (requestId !== requestCounter.current) return;
        setAssets(result.assets);
        setCadStatus("success");
        window.setTimeout(() => setCadStatus("idle"), 1800);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
        if (requestId === requestCounter.current) setCadStatus("error");
      }
    }, immediate ? 0 : 500);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  };

  useEffect(() => requestPreview(), [geometryKey]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2800);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const save = () => {
    localStorage.setItem(
      "cbciso-house-configuration-saved",
      JSON.stringify({ configuration, savedAt: new Date().toISOString() })
    );
    setToast("Konfigurationen er gemt på denne enhed");
  };

  const share = async () => {
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(configuration))));
    const url = new URL(window.location.href);
    url.searchParams.set("config", encoded);
    await navigator.clipboard.writeText(url.toString());
    window.history.replaceState({}, "", url);
    setToast("Delbart link er kopieret");
  };

  return (
    <div className="app-shell">
      <Header onSave={save} onQuote={() => setQuoteOpen(true)} />
      <StepNavigation />
      <div className="configurator-grid">
        <ConfigurationPanel onQuote={() => setQuoteOpen(true)} />
        <HouseViewer modelUrl={assets.model3dUrl} cadStatus={cadStatus} onRetry={() => requestPreview(true)} />
        <SummaryPanel onSave={save} onShare={share} />
      </div>
      <footer className="trust-bar" aria-label="CBCISO fordele">
        <span><i>6–8</i><b>uger</b><small>Hurtig opførelse</small></span>
        <span><i>01</i><b>pris</b><small>Fast og transparent</small></span>
        <span><i>DK</i><b>kvalitet</b><small>Bygget til dansk klima</small></span>
        <span><i>A+</i><b>energi</b><small>Materialer med omtanke</small></span>
      </footer>
      <MobileActionBar onQuote={() => setQuoteOpen(true)} />
      <QuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />
      {toast && <div className="toast" role="status">{toast}</div>}
    </div>
  );
}
